/**
 * Endpoint Inventory — list every registered endpoint with its
 * full last-reported metadata snapshot. Backed by the legacy REST
 * endpoint `GET /v1/endpoints/metadata`.
 *
 * Layout: search + stats stay anchored at the top of the page;
 * column header + data rows share a single overflow-auto scroll
 * container that scrolls in BOTH axes. The col header is
 * `position: sticky; top: 0` inside that container so it stays
 * visible while the body scrolls vertically, and they share the
 * same width so they horizontal-scroll in lock-step.
 *
 * Visibility AND widths are user-controllable + localStorage-
 * persisted via `useColumnSelection` / `useColumnWidths`.
 *
 * The table core (columns, row flattening, cell rendering,
 * filter/sort) lives in components/endpoints/inventoryTable.jsx,
 * shared with the Explore "Inventory" lens.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import ColumnPicker from "../components/ColumnPicker.jsx";
import DataTable from "../components/DataTable.jsx";
import {
    INVENTORY_COLUMNS,
    INVENTORY_DEFAULT_VISIBLE,
    renderInventoryCell,
    fetchInventoryPage,
} from "../components/endpoints/inventoryTable.jsx";
import AdminViewHeader from "../components/AdminViewHeader.jsx";
import SplitPage from "../components/SplitPage.jsx";
import TabStrip from "../components/TabStrip.jsx";
import { renderTraceCell, TRACE_COLUMNS_3 } from "../components/TraceRow.jsx";
import { useQuery } from "../lib/dataClient.js";
import InstalledAgentsDetail from "../components/endpoints/InstalledAgentsDetail.jsx";
import EndpointAssignmentsSection from "../components/endpoints/EndpointAssignmentsSection.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { DetailField, DetailSection } from "../components/detailPrimitives.jsx";
import { formatCompact } from "../lib/renderers.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { skillsFeatureEnabled } from "../lib/featureFlags.js";
import EndpointUpdateRolloutPanel from "../components/endpoints/EndpointUpdateRolloutPanel.jsx";
import EndpointSkillUsagePanel from "../components/endpoints/EndpointSkillUsagePanel.jsx";
import { fullTimestampFormatter } from "../lib/traceUtils.js";
import {
    applyEndpointDedupe,
    endpointDedupeMergedCount,
    endpointIdentityBadge,
} from "../lib/endpointDedupe.js";
import EmptyState, { ConnectEndpointsAction, useTenantEmpty } from "../components/EmptyState.jsx";

// ── Detail drawer ────────────────────────────────────────────────
//
// Mirrors the directory browser's SplitPage drawer: clicking a row
// opens a resizable right pane with the endpoint's full metadata
// snapshot and an "Open in Explore" deeplink into that endpoint's
// top-level scope (see `onScopeNavigate` → App.openInExplore).

// Most-recent traces for the selected endpoint — a compact peek that
// mirrors the trace listing's row chrome (TRACE_COLUMNS_3). Scoped to
// the single endpoint over the app's default window; the "Open in
// Explore" button below jumps to the full, range-aware trace list.
function RecentTraces({ endpointId }) {
    const { data: traces = [], loading, error } = useQuery("getTraces", {
        scopePath: [{ dim: "endpoints", id: endpointId }],
        sort: "newest",
        limit: 3,
        range: "1M",
    });
    const recent = traces.slice(0, 3);

    return (
        <div className="grid gap-[8px]">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                Recent traces
            </span>
            {loading && recent.length === 0 ? (
                <span className={`${NOTE_CLASS} text-[11px] animate-pulse`}>Loading traces…</span>
            ) : error ? (
                <span className="text-[11px] text-red-400">Couldn’t load traces.</span>
            ) : recent.length === 0 ? (
                <span className={`${NOTE_CLASS} text-[11px]`}>No traces in the last month.</span>
            ) : (
                <ul className="grid gap-[3px]">
                    {recent.map((t) => (
                        <li
                            key={t.id}
                            className="grid items-center gap-[8px] border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] py-[6px] [grid-template-columns:64px_minmax(0,1fr)_104px]"
                        >
                            {renderTraceCell(t, TRACE_COLUMNS_3[0], { isCtx: false })}
                            {renderTraceCell(t, TRACE_COLUMNS_3[1], { isCtx: false })}
                            {renderTraceCell(t, TRACE_COLUMNS_3[2], { isCtx: false })}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const BROWSER_COVERAGE_LABELS = {
    ready: "Ready",
    extension_not_installed: "Extension not installed",
    native_host_not_registered: "Native host not registered",
    normal_access_denied: "Normal access denied",
    private_access_denied: "Private access denied",
    unsupported_browser: "Unsupported browser",
    degraded: "Degraded",
    unspecified: "Unknown",
};

function browserAccessLabel(value) {
    if (value === true) return "Allowed";
    if (value === false) return "Denied";
    return "Unknown";
}

function browserRegistrationLabel(value) {
    if (value === true) return "Registered";
    if (value === false) return "Not registered";
    return "Unknown";
}

function BrowserCaptureHealthSection({ endpointId }) {
    const { data, loading, error } = useQuery("getBrowserCaptureHealth", { endpointId });
    const statuses = data?.statuses ?? [];

    return (
        <>
            <div className="border-t border-[color:var(--line)]" />
            <DetailSection
                title="Browser capture"
                busy={loading}
                note={
                    loading && statuses.length === 0 ? "Loading..."
                    : error ? "Could not load browser capture health."
                    : statuses.length === 0 ? "Not reported by this Agent."
                    : null
                }
                columns={1}
            >
                {statuses.map((status) => {
                    const droppedEvents = status.counters.drops.reduce((sum, entry) => sum + entry.eventCount, 0);
                    const failures = status.counters.failures.reduce((sum, entry) => sum + entry.count, 0);
                    return (
                        <div key={status.browserFamily} className="grid gap-[8px] border border-[color:var(--line-soft)] bg-[color:var(--bg)] p-[8px]">
                            <div className="flex items-center justify-between gap-[8px]">
                                <span className="text-[11px] font-medium capitalize text-[color:var(--ink)]">{status.browserFamily}</span>
                                <span className={`${NOTE_CLASS} text-[10px]`}>{BROWSER_COVERAGE_LABELS[status.coverageState] ?? "Unknown"}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-[10px] min-w-0">
                                <DetailField label="Extension" value={status.extensionVersion || (status.extensionInstalled === false ? "Not installed" : "Unknown")} />
                                <DetailField label="Native host" value={browserRegistrationLabel(status.nativeHostRegistered)} />
                                <DetailField label="Normal access" value={browserAccessLabel(status.normalAccessAllowed)} />
                                <DetailField label="Private access" value={browserAccessLabel(status.privateAccessAllowed)} />
                                <DetailField label="Hooked tabs" value={`${formatCompact(status.hookedTabs)} / ${formatCompact(status.eligibleTabs)}`} />
                                <DetailField label="Pending parse failures" value={formatCompact(status.pendingFailedCaptures)} />
                                <DetailField label="Unresolved gaps" value={formatCompact(status.counters.reconciliationUnresolvedGaps)} />
                                <DetailField label="Drops / failures" value={`${formatCompact(droppedEvents)} / ${formatCompact(failures)}`} />
                            </div>
                        </div>
                    );
                })}
            </DetailSection>
        </>
    );
}

function EndpointDetailContent({ row, onScopeNavigate }) {
    const user = useCurrentUser();
    // Observed-skills panel is admin-only and behind the Skills feature flag
    // (same gate as the Skills admin view).
    const skillsAdmin =
        skillsFeatureEnabled() &&
        Boolean(user?.isPlatformAdmin || user?.currentTenant?.role === "TENANT_ROLE_ADMIN");
    const memory = row.systemMemoryMb != null
        ? (Number(row.systemMemoryMb) / 1024 >= 1
            ? `${(Number(row.systemMemoryMb) / 1024).toFixed(1)} GB`
            : `${row.systemMemoryMb} MB`)
        : "";
    const lastSeen = row.snapshotTime instanceof Date
        ? fullTimestampFormatter.format(row.snapshotTime)
        : "";
    const identityBadge = endpointIdentityBadge(row);

    return (
        <div className="grid gap-[16px]">
            {row.id ? <RecentTraces endpointId={row.id} /> : null}

            {onScopeNavigate && row.id ? (
                <button
                    type="button"
                    onClick={() => onScopeNavigate([{ dim: "endpoints", id: row.id }])}
                    className="flex w-full items-center justify-between gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[7px] text-left text-[12px] font-medium text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] cursor-pointer"
                >
                    <span>Open in Explore</span>
                    <span aria-hidden className="text-[color:var(--muted)]">→</span>
                </button>
            ) : null}

            <DetailSection title="Identity">
                {identityBadge ? (
                    <span
                        title={identityBadge.title}
                        className="w-fit rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--control-bg)] px-[5px] py-[2px] text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted)]"
                    >
                        {identityBadge.label}
                    </span>
                ) : null}
                <DetailField label="Hostname" value={row.hostname} />
                <DetailField label="Registered user" value={row.registeredUser} />
                <DetailField label="Operating system" value={row.platform} />
                <DetailField label="OS version" value={row.osDisplayVersion} />
                <DetailField label="OS edition" value={row.osEdition} />
                <DetailField label="Serial" value={row.serial} mono />
                {row.forkedFromEndpointId ? (
                    <DetailField label="Split from" value={row.forkedFromEndpointId} mono />
                ) : null}
            </DetailSection>

            <div className="border-t border-[color:var(--line)]" />

            <DetailSection title="Agent">
                <DetailField label="Agent version" value={row.agentVersion} />
                <DetailField label="Watchdog version" value={row.watchdogVersion} />
                <DetailField label="Last seen" value={lastSeen} />
                <DetailField label="Endpoint ID" value={row.id} mono />
                <DetailField label="Agent SHA256" value={row.agentExecutableSha256} mono />
                <DetailField label="Watchdog SHA256" value={row.watchdogExecutableSha256} mono />
            </DetailSection>

            {row.id ? <BrowserCaptureHealthSection endpointId={row.id} /> : null}

            <div className="border-t border-[color:var(--line)]" />

            {row.id && <EndpointUpdateRolloutPanel key={row.id} endpointId={row.id} />}

            <DetailSection title="Hardware">
                <DetailField label="Manufacturer" value={row.manufacturer} />
                <DetailField label="Model" value={row.model} />
                <DetailField label="Processor" value={row.processorModel} />
                <DetailField label="Cores" value={row.coreCount != null ? String(row.coreCount) : ""} />
                <DetailField label="Memory" value={memory} />
                <DetailField label="Drive size" value={row.primaryDriveDiskSizeGb != null ? `${row.primaryDriveDiskSizeGb} GB` : ""} />
                <DetailField label="Drive free" value={row.primaryDriveFreeSpaceGb != null ? `${row.primaryDriveFreeSpaceGb} GB` : ""} />
            </DetailSection>

            {skillsAdmin && row.idRaw ? (
                <>
                    <div className="border-t border-[color:var(--line)]" />
                    <EndpointSkillUsagePanel endpointId={row.idRaw} isAdmin={skillsAdmin} />
                </>
            ) : null}

            {row.installedAiAgentsList?.length ? (
                <>
                    <div className="border-t border-[color:var(--line)]" />
                    <div className="grid gap-[8px]">
                        <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]`}>
                            Installed AI agents
                        </span>
                        <InstalledAgentsDetail agents={row.installedAiAgentsList} />
                    </div>
                </>
            ) : null}

            {row.idRaw ? <EndpointAssignmentsSection endpointId={row.idRaw} endpointLabel={row.hostname} /> : null}
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────


export default function EndpointInventoryPage({ viewDef, onScopeNavigate = null }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    // Search is applied SERVER-SIDE: the query filters the whole tenant, not
    // just the loaded rows. Debounce so a keystroke burst fires one request,
    // and reset paging in the SAME commit the debounced term settles (via
    // onSettle) so a page-0 replace — not an append onto stale rows — always
    // follows a query change.
    const resetToFirstPage = useCallback(() => setPage(0), []);
    const [debouncedSearch] = useDebouncedValue(search, 300, resetToFirstPage);

    // Selected row drives the detail drawer (SplitPage). Clicking the
    // already-selected row toggles it closed, matching the directory
    // browser. We hold the full flattened row so the drawer keeps
    // rendering even if a search filter would drop it from the table.
    const [selected, setSelected] = useState(null);
    const onRowClick = useCallback((row) => {
        setSelected((cur) => (cur?.id === row.id ? null : row));
    }, []);
    const closeSelection = useCallback(() => setSelected(null), []);

    // Sort is applied SERVER-SIDE (see fetchInventoryPage): the ordering covers
    // the whole tenant, not just the loaded page. Default to most-recently-seen
    // first. Clicking a column header sorts ascending; clicking the active column
    // again toggles direction. A sort change is a fresh query, so it resets
    // paging to page 0 (a page-0 replace, never an append onto rows in a
    // different order) — the same discipline as a search change.
    const [sortBy, setSortBy] = useState({ id: "snapshotTime", dir: "desc" });
    const onSortChange = useCallback((id) => {
        setPage(0);
        setSortBy((cur) =>
            cur?.id === id
                ? { id, dir: cur.dir === "asc" ? "desc" : "asc" }
                : { id, dir: "asc" },
        );
    }, []);

    const cols = useDataTableColumns({
        storageKey: "endpointInventory",
        columns: INVENTORY_COLUMNS,
        defaultVisible: INVENTORY_DEFAULT_VISIBLE,
    });

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        // Page 0 replaces (a fresh query or first load); later pages append.
        // A query change resets page to 0 (see the debounce effect), so the
        // new result set never appends onto rows from the previous search.
        fetchInventoryPage(page, debouncedSearch, sortBy)
            .then(({ rows: list, total: t }) => {
                if (cancelled) return;
                setRows((prev) => (page === 0 ? list : [...prev, ...list]));
                setTotal(t ?? list.length);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("[endpoint inventory]", err);
                setError(err.message || String(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, debouncedSearch, sortBy]);

    // Collapse ordinary rows recognized as the same device while preserving
    // independently allocated clone/conflict identities. Raw rows still drive
    // pagination; only the displayed set is grouped.
    const dedupedRows = useMemo(() => applyEndpointDedupe(rows), [rows]);
    // "shown" counts deduped rows while "reporting metadata" and "Load more"
    // count raw server rows, so the collapse has to be disclosed or the two
    // numbers look like missing machines.
    const mergedCount = endpointDedupeMergedCount(rows.length, dedupedRows.length);
    // Server returns rows already ordered by `sortBy` (see fetchInventoryPage);
    // applyEndpointDedupe preserves row order, so the server ordering survives
    // identity grouping. sortBy still flows to DataTable for the active-column
    // caret — it no longer sorts client-side.

    const isFiltering = debouncedSearch.trim().length > 0;
    // Resolve the tenant-connected question only once the table is actually
    // empty — a populated fleet never spends the fetch.
    const tenantEmpty = useTenantEmpty({ enabled: !loading && dedupedRows.length === 0 });

    const header = (
        <AdminViewHeader
            title={viewDef?.label ?? "Endpoint Inventory"}
            scope="All endpoints"
            actions={<ColumnPicker {...cols.pickerProps} />}
        />
    );

    // Left column: search + stats anchored at the top, table scrolls.
    // h-full (not flex-1): SplitPage's column content slot is a plain
    // block, so flex-1 has no flex parent to size against — the table's
    // self-scroll wrapper would then have no bounded height and the page
    // wouldn't scroll. h-full fills that block; the table wrapper below
    // takes flex-1 within this flex-col so its overflow-auto can scroll.
    const leftContent = (
        <div className="flex h-full min-h-0 min-w-0 flex-col py-[8px]">
            <div className="shrink-0 grid gap-[8px] pb-[2px]">
                <label className="grid gap-[5px]">
                    <span className={NOTE_CLASS}>Search</span>
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Hostname, agent version, registered user, model, OS, …"
                        className="min-h-[30px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)]"
                    />
                </label>

                <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px] border-y border-[color:var(--line-soft)] py-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                    <span>
                        <strong className="text-[color:var(--ink)]">{formatCompact(dedupedRows.length)}</strong> shown
                    </span>
                    {mergedCount > 0 ? (
                        <span title="These rows were recognized as the same device from authoritative identity data, with a conservative serial fallback for older agents.">
                            <strong className="text-[color:var(--ink)]">{formatCompact(mergedCount)}</strong> rows recognized as the same device
                        </span>
                    ) : null}
                    {isFiltering ? (
                        <span>
                            <strong className="text-[color:var(--ink)]">{formatCompact(total ?? 0)}</strong> matching
                        </span>
                    ) : total != null ? (
                        <span title="Endpoints that reported a metadata snapshot, including revoked endpoints. The active fleet count under Identity → Your access excludes revoked endpoints.">
                            <strong className="text-[color:var(--ink)]">{formatCompact(total)}</strong> reporting metadata
                        </span>
                    ) : null}
                    {loading ? <span className={NOTE_CLASS}>Loading…</span> : null}
                    {error ? <span className="text-red-400">{error}</span> : null}
                </div>
            </div>

            <div className="flex flex-1 min-h-0 min-w-0 flex-col">
            <DataTable
                cols={cols}
                rows={dedupedRows}
                getRowId={(r) => r.id || r.hostname}
                renderCell={renderInventoryCell}
                onRowClick={onRowClick}
                sortBy={sortBy}
                onSortChange={onSortChange}
                loading={loading}
                skeletonRows={6}
                empty={
                    isFiltering ? (
                        <EmptyState
                            className="my-[12px]"
                            title="No endpoints match this search"
                            note="No endpoints match this search. Try a different term."
                        />
                    ) : tenantEmpty ? (
                        <EmptyState
                            className="my-[12px]"
                            title="Origin isn't connected to any endpoints yet"
                            note="Install the agent on one machine and it appears here within about a minute."
                            action={<ConnectEndpointsAction />}
                        />
                    ) : (
                        <EmptyState
                            className="my-[12px]"
                            title="No endpoints registered"
                            note="Endpoints appear here once their agents check in."
                        />
                    )
                }
                footer={
                    total != null && rows.length < total ? (
                        <div className="grid place-items-center pt-[8px] pb-[10px]">
                            <button
                                type="button"
                                onClick={() => setPage((p) => p + 1)}
                                disabled={loading}
                                className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[4px] text-[11px] leading-[1.08] tracking-[0.04em] uppercase text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 cursor-pointer"
                            >
                                Load more · {formatCompact(total - rows.length)} remaining
                            </button>
                        </div>
                    ) : null
                }
            />
            </div>
        </div>
    );

    // Right column: single-tab header carrying the selected hostname
    // (SplitPage injects the close chevron into its actions slot) +
    // the metadata detail body.
    const detailTabStrip = (
        <TabStrip
            tabs={[{
                id: "detail",
                label: selected?.hostname || selected?.id || "Endpoint",
                active: true,
                dot: "bg-[color:var(--green)]",
            }]}
            className="pt-0"
        />
    );

    const rightContent = () => (
        <div className="flex h-full min-h-0 min-w-0 flex-col">
            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto py-[12px]">
                {selected ? (
                    <EndpointDetailContent row={selected} onScopeNavigate={onScopeNavigate} />
                ) : null}
            </div>
        </div>
    );

    return (
        // AdminPage wraps the view in a px-[var(--main-inset)] section and
        // SplitPage's ContentShell adds the same inset per column — the
        // outer one would double up at the edges. Negative margin lets the
        // column-level inset run alone (matches DirectoryBrowserTab).
        <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
            <SplitPage
                defaultDetailRatio={0.42}
                focusHeader={header}
                leftContent={leftContent}
                rightHeader={detailTabStrip}
                rightContent={rightContent}
                forceCollapse={!selected}
                onCloseDetail={closeSelection}
            />
        </div>
    );
}
